// =============================================================================
// Lift study PDF — T-436 / T-438
// =============================================================================
// Rebuilt 01/08 after James read v1 side by side with Borger's Park Ridge study.
// What was wrong with v1, and what this fixes:
//
//   * Page 1 was two-thirds empty with "ELEVATION" orphaned at the bottom and
//     the drawing stranded alone on page 2  ->  real cover page, and every
//     heading is glued to its content so nothing orphans.
//   * Stage blocks split mid-table across pages  ->  each stage is unbreakable.
//   * The canvas hint "drag the compound..." printed into the client's copy
//     ->  stripped at export (see svgToPng / .lp-noexport).
//   * No scope, no site address, no date, no crane company, no methodology, no
//     hazards (they were captured and silently never printed)  ->  all in.
//   * Ground bearing printed 0 t / 5 kPa rows, which is worse than printing
//     nothing  ->  only rendered once real outrigger loads are entered.
//   * One elevation for the whole study  ->  one per stage.
//
// Document order: cover / brief + scope / sequence + hazards / plan / elevations
// / stage data sheets / ground bearing / crew + environment / approvals.
// =============================================================================

import pdfmakeScriptUrl from "pdfmake/build/pdfmake.min.js?url";
// @ts-ignore vfs fonts has no types
import vfsFontsScriptUrl from "pdfmake/build/vfs_fonts.js?url";
import { svgToPng } from "./LiftPlanCanvas";
import { fmtKg, bearingPressureKpa, bearingVerdict } from "./liftPlanCalc";

// Issuer details for client-facing copies. Kept here as one block so there's a
// single place to correct them — and so a logo can be dropped in later without
// hunting through the layout (James, 01/08).
// Verified 02/08 from two independent sources that agree: the Xero organisation
// record (company_registration_number 63690407679) and the coretelgroup.com
// footer (ABN 63 690 407 679 · registered office).
export const COMPANY = {
  name: "Coretel Group Pty Ltd",
  abn: "ABN 63 690 407 679",
  address: "Unit 10, 13 Feeder Street, Narangba QLD 4504",
  phone: "0455 046 410",
  email: "admin@coretelgroup.com",         // CONFIRM — site email is Cloudflare-obfuscated
  web: "coretelgroup.com",
  /** Optional PNG/JPEG data URL. When set it replaces the wordmark. */
  logoDataUrl: null as string | null,
};

const C = {
  accent: "#FF6A1A", ink: "#111111", text: "#222222", muted: "#666666",
  panel: "#F6F6F4", rule: "#E2E1DD", paper: "#FBFAF7",
  ok: "#1D8348", warn: "#B7950B", danger: "#CB4335",
};

function loadScriptOnce(src: string, tag: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[data-pdfmake-tag="${tag}"]`);
    if (existing) {
      if (existing.dataset.loaded === "true") resolve();
      else { existing.addEventListener("load", () => resolve()); existing.addEventListener("error", () => reject(new Error(`reload ${tag}`))); }
      return;
    }
    const s = document.createElement("script");
    s.src = src; s.async = true; s.dataset.pdfmakeTag = tag;
    s.onload = () => { s.dataset.loaded = "true"; resolve(); };
    s.onerror = () => reject(new Error(`load ${tag}`));
    document.head.appendChild(s);
  });
}

async function getPdfMake(): Promise<any> {
  const existing = (window as any).pdfMake;
  if (existing?.vfs && Object.keys(existing.vfs).length > 0) return existing;
  await loadScriptOnce(pdfmakeScriptUrl, "pdfmake-core");
  await loadScriptOnce(vfsFontsScriptUrl, "pdfmake-vfs");
  const pdfMake = (window as any).pdfMake;
  if (!pdfMake) throw new Error("pdfMake missing");
  return pdfMake;
}

const fmtDate = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const dash = (s?: string | null) => (s && String(s).trim()) || "—";

/** Section heading that can never be left stranded at the foot of a page. */
const section = (title: string, body: any[], topGap = 14) => ({
  stack: [
    { text: title, bold: true, fontSize: 9.5, color: C.accent, characterSpacing: 0.6, margin: [0, 0, 0, 5] },
    { canvas: [{ type: "line", x1: 0, y1: 0, x2: 781, y2: 0, lineWidth: 0.7, lineColor: C.accent }], margin: [0, -3, 0, 6] },
    ...body,
  ],
  margin: [0, topGap, 0, 0],
  // No headlineLevel — it made pdfmake shunt a whole section to the next page
  // rather than break it, which left page 3 almost entirely blank (James, 01/08).
});

function utilCell(pct: number | null) {
  if (pct == null) return { text: "OFF CHART", color: "#fff", fillColor: C.danger, bold: true, alignment: "center", fontSize: 8 };
  const colour = pct > 100 ? C.danger : pct > 90 ? C.danger : pct > 75 ? C.warn : C.ok;
  return { text: `${pct.toFixed(0)}%`, color: "#fff", fillColor: colour, bold: true, alignment: "center", fontSize: 8 };
}

const kv = (label: string, value: string) => ([
  { text: label, bold: true, fontSize: 6.8, color: C.muted, characterSpacing: 0.4 },
  { text: value || "—", fontSize: 8.5 },
]);

const tableLayout = {
  hLineColor: () => C.rule, vLineColor: () => C.rule,
  hLineWidth: () => 0.5, vLineWidth: () => 0.5,
  paddingTop: () => 3, paddingBottom: () => 3, paddingLeft: () => 5, paddingRight: () => 5,
};

const th = (text: string, alignment: string = "left") =>
  ({ text, bold: true, fontSize: 7.2, color: "#fff", fillColor: C.ink, alignment, characterSpacing: 0.4 });

export async function exportLiftPlanPdf(opts: {
  planNumber: string; title: string; version: number; status: string;
  projectCode?: string | null; siteName?: string | null; clientName?: string | null;
  approvedBy?: string | null; approvedAt?: string | null;
  preparedBy?: string | null;
  content: any;
  stages: { stage: any; result: any; config: any }[];
  /** DOM ids of one elevation SVG per stage, in stage order. */
  elevationIds?: string[];
  /** DOM ids of one plan SVG per stage — each carries that lift's pick point. */
  planIds?: string[];
}) {
  const pdfMake = await getPdfMake();
  const isDraft = opts.status !== "approved";
  const brief = opts.content?.brief ?? {};
  const config = opts.stages[0]?.config ?? null;
  const craneName = config ? `${config.crane_models?.make ?? ""} ${config.crane_models?.model ?? ""}`.trim() : "";

  const planPng = await svgToPng("lp-plan", 2).catch(() => null);
  const elevPngs: (string | null)[] = [];
  for (const id of opts.elevationIds ?? ["lp-elev"]) {
    elevPngs.push(await svgToPng(id, 2).catch(() => null));
  }
  // One plan per lift, each with its own pick point.
  const stagePlanPngs: (string | null)[] = [];
  for (const id of opts.planIds ?? []) {
    stagePlanPngs.push(await svgToPng(id, 2).catch(() => null));
  }

  // The governing stage is the one a reviewer turns to first.
  const worst = opts.stages.reduce<{ i: number; pct: number } | null>((acc, s, i) => {
    const p = s.result?.pctChart;
    if (p == null) return acc;
    return acc == null || p > acc.pct ? { i, pct: p } : acc;
  }, null);

  // ---------------------------------------------------------------- cover ----
  const statusBanner = {
    table: { widths: ["*"], body: [[{
      text: isDraft
        ? "DRAFT — NOT FOR CONSTRUCTION. This study is not approved and must not be worked to."
        : brief.purpose === "engaged"
          ? "APPROVED — PLANNING ONLY. The crane contractor's own lift study governs on the day."
          : "ISSUED FOR TENDER — INDICATIVE METHODOLOGY. Not engaged, not for construction.",
      color: "#fff", bold: true, fontSize: 9, alignment: "center", margin: [0, 6, 0, 6],
      fillColor: isDraft ? C.danger : C.ok,
    }]] },
    layout: "noBorders",
    margin: [0, 0, 0, 16],
  };

  const coverRow = (a: string, av: string, b: string, bv: string) => ([
    { text: a, bold: true, fontSize: 7, color: C.muted, characterSpacing: 0.5 },
    { text: av, fontSize: 10 },
    { text: b, bold: true, fontSize: 7, color: C.muted, characterSpacing: 0.5 },
    { text: bv, fontSize: 10 },
  ]);

  const cover: any[] = [
    COMPANY.logoDataUrl
      ? { image: COMPANY.logoDataUrl, fit: [230, 62], margin: [0, 26, 0, 6] }
      : { text: "CORETEL GROUP", bold: true, fontSize: 22, color: C.ink, characterSpacing: 1, margin: [0, 30, 0, 0] },
    { text: "LIFT STUDY", fontSize: 11, color: C.accent, characterSpacing: 4, margin: [0, 2, 0, 3] },
    { text: brief.purpose === "engaged"
        ? "PLANNING STUDY — NOT AN AUTHORITY TO LIFT"
        : "PRELIMINARY — FOR TENDER AND PLANNING ONLY. NOT AN AUTHORITY TO LIFT",
      fontSize: 7.5, color: C.muted, characterSpacing: 0.6, margin: [0, 0, 0, 20] },
    { canvas: [{ type: "line", x1: 0, y1: 0, x2: 781, y2: 0, lineWidth: 2.5, lineColor: C.accent }], margin: [0, 0, 0, 18] },
    { text: opts.title, bold: true, fontSize: 20, color: C.ink, margin: [0, 0, 0, 4] },
    { text: `${opts.planNumber}  ·  Revision ${opts.version}`, fontSize: 11, color: C.accent, margin: [0, 0, 0, 22] },
    statusBanner,
    {
      table: {
        widths: ["auto", "*", "auto", "*"],
        body: [
          coverRow("CLIENT", dash(opts.clientName), "PROJECT / BID", dash(opts.projectCode)),
          coverRow("SITE", dash(opts.siteName), "SITE ADDRESS", dash(brief.site_address)),
          coverRow("PLANNED DATE", dash(brief.lift_date), "LIFT CLASSIFICATION", dash(brief.lift_class)),
          coverRow("CRANE", craneName || "—", "CRANE HIRE", dash(brief.crane_company)),
          coverRow("LIFTS IN THIS STUDY", String(opts.stages.length),
            "GOVERNING CASE", worst ? `Stage ${worst.i + 1} — ${worst.pct.toFixed(0)}% of chart` : "—"),
        ],
      },
      layout: {
        fillColor: (i: number) => (i % 2 === 0 ? C.panel : null),
        hLineWidth: () => 0, vLineWidth: () => 0,
        paddingTop: () => 7, paddingBottom: () => 7, paddingLeft: () => 8, paddingRight: () => 8,
      },
      margin: [0, 0, 0, 20],
    },
    {
      table: {
        headerRows: 1,
        widths: ["auto", "*", "*", "auto"],
        body: [
          [th("REV"), th("PREPARED BY"), th("APPROVED BY (DIRECTOR)"), th("DATE", "center")],
          [
            { text: String(opts.version), fontSize: 8.5, alignment: "center" },
            { text: dash(opts.preparedBy), fontSize: 8.5 },
            { text: dash(opts.approvedBy), fontSize: 8.5 },
            { text: fmtDate(opts.approvedAt), fontSize: 8.5, alignment: "center" },
          ],
        ],
      },
      layout: tableLayout,
    },
    // Limitation of reliance. James, 02/08: Coretel is not a crane operator and
    // not a certifying engineer — this document plans a lift, it does not
    // authorise one. See the note in chat: this limits RELIANCE, it does not
    // and cannot transfer a PCBU's WHS duty.
    {
      table: { widths: ["*"], body: [[{
        stack: [
          { text: "PURPOSE AND LIMITATIONS", bold: true, fontSize: 7.5, color: C.ink, characterSpacing: 0.5, margin: [0, 0, 0, 3] },
          { text: brief.purpose === "engaged"
              ? "This document is a PLANNING STUDY ONLY, prepared for works Coretel Group Pty Ltd has been engaged to deliver. It sizes and sequences a proposed lift and informs the engagement of a licensed crane contractor. It is not a certified lift plan, not an engineering certification, and it does not authorise any lifting operation to proceed."
              : "This document is issued FOR TENDER as an indicative methodology. Coretel Group Pty Ltd has not been engaged to perform these works and no order has been placed. It illustrates a proposed approach and the crane class the work is expected to require, for pricing and comparison only. It is not a certified lift plan, not an engineering certification, and it does not authorise any lifting operation to proceed.",
            fontSize: 7.2, color: C.text, lineHeight: 1.25 },
          { text: "Coretel Group Pty Ltd is not a licensed crane owner or crane operator and does not hold itself out as a certifying engineer. Capacities shown are read from published manufacturer load charts and are indicative. Unless expressly marked otherwise, charts used are GENERIC manufacturer data, not the chart of the machine to be supplied.",
            fontSize: 7.2, color: C.text, lineHeight: 1.25, margin: [0, 3, 0, 0] },
          // Weight provenance is PER PLAN, not a blanket statement — on a job
          // with issued FCs the weights are certified and saying otherwise
          // would undersell the document (James, 02/08).
          ...(brief.weights_basis === "indicative" ? [{
            text: `PIECE WEIGHTS ARE INDICATIVE. ${brief.weights_note?.trim()
              || "Weights are taken from comparable previously-supplied structures of the same type and are used for planning and pricing only."} They are not certified weights, and Final For-Construction drawings carrying the structural certification and pole schedule have not been issued for this site at the date of this document. All weights, and every capacity and utilisation figure derived from them, must be re-verified against the issued FC drawings and the supplier's delivery documentation before any lift is planned in detail or performed.`,
            fontSize: 7.2, color: C.danger, lineHeight: 1.25, margin: [0, 3, 0, 0],
          }] : []),
          { text: "Before any lift proceeds, the crane contractor must produce their own lift study for the actual machine and configuration, verified against that machine's load chart, ground conditions and site constraints on the day. The crane contractor's study and the crane's rated capacity indicator govern absolutely and prevail over this document in the event of any inconsistency. Coretel Group Pty Ltd accepts no liability for reliance on this document by any party for the execution of a lift.",
            fontSize: 7.2, color: C.text, lineHeight: 1.25, margin: [0, 3, 0, 0] },
        ],
        fillColor: C.paper, margin: [9, 7, 9, 7],
      }]] },
      layout: { hLineWidth: () => 0.7, vLineWidth: () => 0.7, hLineColor: () => C.rule, vLineColor: () => C.rule },
      margin: [0, 0, 0, 0],
      // Its own page. It was breaking mid-paragraph across pages 1 and 2, which
      // looked like a mistake on the one panel that most needs to look
      // deliberate (James, 03/08).
      pageBreak: "before",
      unbreakable: true,
    },
    // Issuer block — this goes to clients, so it has to carry who issued it.
    { canvas: [{ type: "line", x1: 0, y1: 0, x2: 781, y2: 0, lineWidth: 0.7, lineColor: C.rule }], margin: [0, 12, 0, 8] },
    {
      columns: [
        { width: "*", stack: [
          { text: COMPANY.name, bold: true, fontSize: 9, color: C.ink },
          { text: COMPANY.abn, fontSize: 7.5, color: C.muted },
        ] },
        { width: "*", stack: [
          { text: COMPANY.address, fontSize: 7.5, color: C.muted },
          { text: COMPANY.phone, fontSize: 7.5, color: C.muted },
        ] },
        { width: "*", stack: [
          { text: COMPANY.email, fontSize: 7.5, color: C.muted },
          { text: COMPANY.web, fontSize: 7.5, color: C.accent },
        ] },
      ],
      columnGap: 16,
    },
  ];

  // ------------------------------------------------------------- the brief ---
  const briefBody: any[] = [
    {
      columns: [
        { width: "*", table: { widths: ["auto", "*"], body: [
          kv("SITE ADDRESS", dash(brief.site_address)),
          kv("SITE ACCESS & HARDSTAND", dash(brief.access)),
          kv("PLANNED DATE", dash(brief.lift_date)),
          kv("EXPECTED DURATION", dash(brief.duration)),
          kv("LIFT CLASSIFICATION", dash(brief.lift_class)),
        ] }, layout: "noBorders" },
        { width: "*", table: { widths: ["auto", "*"], body: [
          kv("CRANE HIRE COMPANY", dash(brief.crane_company)),
          kv("CRANE CONTACT", dash(brief.crane_contact)),
          kv("STRUCTURE", `${opts.content?.canvas?.structure ?? "—"} · ${opts.content?.canvas?.structure_height_m ?? "—"} m`),
          kv("EMERGENCY / MUSTER", dash(brief.emergency)),
          kv("STANDARDS", "AS 1418.5 — Mobile cranes · AS 2550.1 — Safe use, general requirements · AS 2550.5 — Safe use, mobile cranes"),
          kv("WHS", "The Work Health and Safety Act and Regulations of the State or Territory in which the work is performed, together with the applicable Codes of Practice (managing risks of plant, construction work, falls, excavation, and work near overhead and underground electric lines)."),
          kv("PROJECT REFERENCES", dash(brief.references)),
          kv("CURRENCY", "Editions current at the date of issue are to be confirmed by the document controller before this study is issued."),
        ] }, layout: "noBorders" },
      ],
      columnGap: 20,
    },
  ];

  const scopeBody = [{
    table: { widths: ["*"], body: [[{
      text: dash(brief.scope), fontSize: 9, lineHeight: 1.25, margin: [8, 7, 8, 7], fillColor: C.paper,
    }]] },
    layout: { hLineWidth: () => 0.5, vLineWidth: () => 0.5, hLineColor: () => C.rule, vLineColor: () => C.rule },
  }];

  const seq: string[] = (opts.content?.sequence ?? []).filter((s: string) => s && s.trim());
  const sequenceBody = seq.length
    ? [{
        table: {
          widths: ["auto", "*"],
          body: seq.map((s, i) => ([
            { text: String(i + 1).padStart(2, "0"), bold: true, fontSize: 8.5, color: C.accent, alignment: "center" },
            { text: s, fontSize: 8.8, lineHeight: 1.2 },
          ])),
        },
        layout: {
          hLineColor: () => C.rule, vLineColor: () => C.rule,
          hLineWidth: (i: number) => (i === 0 ? 0 : 0.5), vLineWidth: () => 0,
          paddingTop: () => 4, paddingBottom: () => 4, paddingLeft: () => 6, paddingRight: () => 6,
        },
      }]
    : [{ text: "No sequence recorded.", fontSize: 8.5, color: C.muted, italics: true }];

  const hazards: any[] = (opts.content?.hazards ?? []).filter((h: any) => h?.hazard || h?.control);
  const hazardBody = hazards.length
    ? [{
        table: {
          headerRows: 1,
          widths: ["*", "*"],
          body: [
            [th("HAZARD"), th("CONTROL")],
            ...hazards.map((h: any) => ([
              { text: dash(h.hazard), fontSize: 8.5 },
              { text: dash(h.control), fontSize: 8.5 },
            ])),
          ],
        },
        layout: tableLayout,
      }]
    : [{ text: "No hazards recorded on this study.", fontSize: 8.5, color: C.muted, italics: true }];

  // ------------------------------------------------------- stage data sheets -
  const stageSheets: any[] = [];
  opts.stages.forEach(({ stage, result, config: cfg }, i) => {
    const rows: any[] = [
      { text: `STAGE ${i + 1} — ${stage.name}`, bold: true, fontSize: 10, color: C.accent, margin: [0, 0, 0, 5] },
      {
        columns: [
          { width: "*", table: { widths: ["auto", "*"], body: [
            kv("CRANE", cfg ? `${cfg.crane_models?.make ?? ""} ${cfg.crane_models?.model ?? ""}` : "—"),
            kv("CONFIGURATION", cfg?.config_name ?? "—"),
            [{ text: "CHART BASIS", bold: true, fontSize: 6.8, color: C.muted },
             { text: cfg?.chart_basis === "machine"
                 ? `Machine chart${cfg?.machine_serial ? ` · ${cfg.machine_serial}` : ""}`
                 : "Generic — manufacturer published (planning)",
               fontSize: 8.5, color: cfg?.chart_basis === "machine" ? C.ok : C.warn }],
            kv("BOOM LENGTH", stage.boom_length_m ? `${stage.boom_length_m} m` : "—"),
            kv("PIECE", stage.piece_desc || "—"),
            kv("PIECE HEIGHT", stage.piece_length_m ? `${stage.piece_length_m} m` : "—"),
            kv("ALREADY ERECTED", `${stage.built_height_m ?? 0} m`),
          ] }, layout: "noBorders" },
          { width: "*", table: { widths: ["auto", "*"], body: [
            kv("PIECE WEIGHT", fmtKg(stage.piece_weight_kg)),
            kv("ALLOWANCES", fmtKg(stage.other_allowance_kg)),
            kv("HOOK + ROPE", fmtKg((stage.hook_kg || 0) + (stage.rope_kg || 0))),
            kv("RIGGING", fmtKg(result.riggingKg)),
            kv("DUAL LIFT FACTOR", stage.dual_lift ? "Yes — 20% applied" : "No"),
            [{ text: "TOTAL LIFT WEIGHT", bold: true, fontSize: 6.8, color: C.accent },
             { text: fmtKg(result.grossKg), fontSize: 10, bold: true }],
            kv("TOP OF STACK", `${((stage.built_height_m ?? 0) + (stage.piece_length_m ?? 0)).toFixed(2)} m`),
          ] }, layout: "noBorders" },
        ],
        columnGap: 20,
      },
      {
        table: {
          headerRows: 1,
          widths: ["*", "auto", "auto", "auto", "auto", "auto"],
          body: [
            [th("STRUCTURAL LIFTING CAPACITY"), th("RADIUS", "center"), th("CHART", "center"),
             th("% CHART", "center"), th("% WINCH", "center"), th("HOOK HT", "center")],
            ...(["Pick", "Place"] as const).map(which => {
              const r = which === "Pick" ? stage.pick_radius_m : stage.place_radius_m;
              const governs = Math.abs((r ?? 0) - result.governingRadiusM) < 0.05;
              return [
                { text: governs ? `${which}  (governs)` : which, fontSize: 8.5, bold: governs },
                { text: governs && result.chartRadiusM != null && Math.abs(result.chartRadiusM - (r ?? 0)) >= 0.05
                    ? `${r?.toFixed(1) ?? "—"} m\nread at ${result.chartRadiusM.toFixed(1)} m`
                    : `${r?.toFixed(1) ?? "—"} m`,
                  fontSize: 8.5, alignment: "center" },
                { text: result.chartKg == null ? "—" : fmtKg(result.chartKg), fontSize: 8.5, alignment: "center" },
                utilCell(result.pctChart),
                { text: result.pctWinch != null ? `${result.pctWinch.toFixed(0)}%` : "—", fontSize: 8.5, alignment: "center" },
                { text: result.hookHeightM != null ? `${result.hookHeightM.toFixed(1)} m` : "—", fontSize: 8.5, alignment: "center" },
              ];
            }),
          ],
        },
        layout: tableLayout,
        margin: [0, 7, 0, 0],
      },
      { text: "Capacity is read at the governing radius — the worse of pick and place. Where a calculated radius falls between charted radii the next greater (less favourable) charted radius is used; capacities are not interpolated and are never extrapolated beyond the chart.",
        fontSize: 6.8, color: C.muted, margin: [0, 3, 0, 0] },
      // Zero rigging is almost never true, and it understates the suspended
      // load on the one number that matters (external review, 06/08).
      ...(!(result.riggingKg > 0) ? [{
        text: "RIGGING ALLOWANCE NOT ENTERED — the total lift weight above excludes slings, shackles, lifting clutches, spreader/strongback and tag hardware. Enter the rigging schedule, or a clearly-labelled provisional allowance, before this study is relied on.",
        fontSize: 6.8, color: C.danger, margin: [0, 3, 0, 0],
      }] : []),
    ];

    if ((stage.rigging ?? []).length > 0) {
      // unbreakable: the header used to land at the very bottom of the lift page
      // and drop one or two rows onto an otherwise blank page with no title on
      // it (James, 07/08). Kept together, and self-titled with the lift so it
      // identifies itself wherever it lands.
      rows.push({
        unbreakable: true,
        table: {
          headerRows: 1,
          widths: ["*", "auto", "auto", "auto"],
          body: [
            [th(`RIGGING BILL OF MATERIALS — LIFT ${i + 1}${stage.piece_desc ? ` · ${stage.piece_desc}` : ""}`),
             th("QTY", "center"), th("UNIT", "right"), th("WEIGHT", "right")],
            ...stage.rigging.map((r: any) => ([
              { text: r.item || "—", fontSize: 8 },
              { text: String(r.qty ?? 0), fontSize: 8, alignment: "center" },
              { text: `${r.unit_kg ?? 0} kg`, fontSize: 8, alignment: "right" },
              { text: `${(r.qty ?? 0) * (r.unit_kg ?? 0)} kg`, fontSize: 8, alignment: "right" },
            ])),
          ],
        },
        layout: tableLayout,
        margin: [0, 7, 0, 0],
      });
    }

    if ((result.warnings ?? []).length > 0) {
      rows.push({
        table: { widths: ["*"], body: [[{
          stack: result.warnings.map((w: string) => ({ text: `▲  ${w}`, fontSize: 8, color: C.warn, margin: [0, 1, 0, 1] })),
          fillColor: "#FDF6E3", margin: [8, 5, 8, 5],
        }]] },
        layout: "noBorders",
        margin: [0, 7, 0, 0],
      });
    }

    // Kept as a block per stage so the elevation and its numbers can be paired
    // on one page — see the assembly below.
    stageSheets.push(rows);
  });

  // ------------------------------------------------------- ground bearing ----
  const padRows = (opts.content?.ground?.pads ?? []).filter((p: any) => Number(p.load_t) > 0);
  const pads = padRows.map((p: any) => {
    const kpa = bearingPressureKpa(p.load_t, p.length_m, p.width_m, p.tare_t);
    const v = kpa == null ? null : bearingVerdict(kpa);
    return ([
      { text: p.leg, fontSize: 8 },
      { text: `${p.load_t ?? 0} t`, fontSize: 8, alignment: "center" },
      { text: `${p.length_m} × ${p.width_m} m`, fontSize: 8, alignment: "center" },
      { text: `${((p.length_m || 0) * (p.width_m || 0)).toFixed(2)} m²`, fontSize: 8, alignment: "center" },
      { text: kpa == null ? "—" : `${kpa.toFixed(0)} kPa`, fontSize: 8, alignment: "right", bold: true,
        color: kpa != null && kpa > 200 ? C.danger : kpa != null && kpa > 100 ? C.warn : C.ok },
      { text: v?.label ?? "—", fontSize: 7.5, color: C.muted },
    ]);
  });

  // ------------------------------------------------------------- document ----
  const dd: any = {
    pageSize: "A4",
    pageOrientation: "landscape",
    pageMargins: [30, 62, 30, 38],
    info: { title: `${opts.planNumber} ${opts.title}`, author: "Coretel Group", subject: "Lift study" },
    defaultStyle: { fontSize: 9, color: C.text, lineHeight: 1.15 },
    header: (page: number) => page === 1 ? undefined : ({
      margin: [30, 18, 30, 0],
      stack: [
        {
          columns: [
            { stack: [
              { text: "CORETEL GROUP", bold: true, fontSize: 12, color: C.ink, characterSpacing: 0.5 },
              { text: "LIFT STUDY", fontSize: 7, color: C.accent, characterSpacing: 2 },
            ] },
            { stack: [
              { text: `${opts.planNumber}  ·  Rev ${opts.version}`, alignment: "right", bold: true, fontSize: 9.5, color: C.accent },
              { text: isDraft ? "DRAFT — NOT FOR USE" : "APPROVED", alignment: "right", fontSize: 7.5,
                color: isDraft ? C.danger : C.ok, bold: true },
            ] },
          ],
        },
        { canvas: [{ type: "line", x1: 0, y1: 0, x2: 781, y2: 0, lineWidth: 0.8, lineColor: C.rule }], margin: [0, 6, 0, 0] },
      ],
    }),
    footer: (page: number, pages: number) => ({
      margin: [30, 10, 30, 0],
      columns: [
        { text: `${opts.planNumber} · ${dash(opts.siteName)} · ${dash(opts.clientName)}`, fontSize: 6.5, color: C.muted },
        { text: "PLANNING STUDY ONLY — not a certified lift plan. Uncontrolled once printed. The crane contractor's own study governs.",
          fontSize: 6.5, color: C.muted, alignment: "center" },
        { text: `Page ${page} of ${pages}`, alignment: "right", fontSize: 7, color: C.muted },
      ],
    }),
    content: [
      ...cover,

      { text: "", pageBreak: "before" },
      section("SCOPE OF WORKS", scopeBody, 0),
      section("PROJECT & SITE DETAILS", briefBody),
      section("LIFT SEQUENCE & HOLD POINTS", sequenceBody),
      section("HAZARDS & CONTROLS", hazardBody),

      ...(planPng ? [
        // No forced page break — it left the preceding page mostly white when the
        // hazards table finished early. pdfmake pushes the image itself if it
        // genuinely does not fit (James, 06/08).
        section("CRANE LOCATION PLAN", [
          { image: planPng, width: 700, alignment: "center" },
          { text: `Compound ${opts.content?.canvas?.compound_m ?? "—"} m · exclusion zone ${opts.content?.canvas?.exclusion_r_m ?? "—"} m radius · all dimensions in metres, drawn to a single scale.`,
            fontSize: 7, color: C.muted, alignment: "center", margin: [0, 5, 0, 0] },
        ], 0),
      ] : []),

      // One page per lift: the elevation and the numbers for that lift together.
      // They used to be four near-empty elevation pages followed by a separate
      // data-sheet section, so you had to flip back and forth and half the
      // document was white space (James, 01/08).
      ...opts.stages.flatMap((s, i) => ([
        { text: "", pageBreak: "before" },
        // NOT unbreakable. Marking the whole lift unbreakable dropped Lift 2
        // entirely and emitted two blank pages, because its warning box pushed
        // it over one page and pdfmake then couldn't place it at all (James,
        // 03/08). The right fix is making the content FIT — smaller drawings —
        // not forcing it to stay together.
        // Names often carry their own "Lift n" from before a reorder, which then
        // fights the positional number. Strip that prefix so the heading reads
        // "LIFT 2 — Base Module", not "LIFT 2 — Lift 1 - Base Module".
        section(`LIFT ${i + 1} — ${String(s.stage?.name ?? "").replace(/^\s*lift\s*\d+\s*[-\u2013\u2014:.]?\s*/i, "").trim() || (s.stage?.piece_desc ?? "")}`, [
          // Plan beside elevation, BOTH for this lift. The plan carries this
          // lift's own pick point, so each page shows the actual reach for that
          // pick rather than repeating the study's first one (James, 03/08).
          ...((stagePlanPngs[i] || elevPngs[i]) ? [
            {
              // Elevation LEFT (the lift), plan RIGHT (the pick) — James's
              // preferred reading order, 03/08. Both rendered from a 980x620
              // box so they land the same size.
              columns: [
                elevPngs[i]
                  ? { width: "*", stack: [
                      { text: `ELEVATION — hook ${s.result?.hookHeightM != null ? s.result.hookHeightM.toFixed(1) + " m" : "—"} · top of stack ${((s.stage?.built_height_m ?? 0) + (s.stage?.piece_length_m ?? 0)).toFixed(2)} m`,
                        fontSize: 7, color: C.muted, alignment: "center", margin: [0, 0, 0, 3] },
                      { image: elevPngs[i], width: 302, alignment: "center" },
                    ] }
                  : { width: "*", text: "" },
                stagePlanPngs[i]
                  ? { width: "*", stack: [
                      { text: `PLAN — pick ${s.stage?.pick_radius_m?.toFixed(1) ?? "—"} m · place ${s.stage?.place_radius_m?.toFixed(1) ?? "—"} m`,
                        fontSize: 7, color: C.muted, alignment: "center", margin: [0, 0, 0, 3] },
                      { image: stagePlanPngs[i], width: 302, alignment: "center" },
                    ] }
                  : { width: "*", text: "" },
              ],
              columnGap: 14,
            },
            { text: "Radii, boom angle and hook height are geometric and indicative. The crane's own computer governs on the day.",
              fontSize: 6.8, color: C.muted, alignment: "center", margin: [0, 3, 0, 8] },
          ] : []),
          ...(stageSheets[i] ?? []).slice(1),   // drop the block's own heading
        ], 0),
      ])),

      ...(pads.length ? [
        section("OUTRIGGER LOADS & GROUND BEARING", [
          {
            table: {
              headerRows: 1,
              widths: ["*", "auto", "auto", "auto", "auto", "auto"],
              body: [
                [th("LEG"), th("LOAD", "center"), th("PAD", "center"), th("AREA", "center"),
                 th("PRESSURE", "right"), th("ASSESSMENT")],
                ...pads,
              ],
            },
            layout: tableLayout,
          },
          { text: "Outrigger load × 9.81 ÷ pad area, pad tare included. Ground assessment is indicative — marginal ground requires geotechnical advice.",
            fontSize: 7, color: C.muted, margin: [0, 3, 0, 0] },
          ...(opts.content?.ground?.assessment
            ? [{ text: opts.content.ground.assessment, fontSize: 8.5, margin: [0, 5, 0, 0] }] : []),
        ]),
      ] : [
        section("OUTRIGGER LOADS & GROUND BEARING", [
          { text: "Outrigger reactions not yet supplied. Obtain them from the crane owner's study and confirm pad sizes before mobilising.",
            fontSize: 8.5, color: C.warn, italics: true },
        ]),
      ]),

      section("CREW & WORK ENVIRONMENT", [
        {
          columns: [
            { width: "*", table: { widths: ["auto", "*"], body: [
              kv("CRANE OPERATOR", opts.content?.personnel?.operator ?? ""),
              kv("DOGMAN", opts.content?.personnel?.dogman ?? ""),
              kv("RIGGERS", opts.content?.personnel?.riggers ?? ""),
              kv("LIFT SUPERVISOR", opts.content?.personnel?.supervisor ?? ""),
            ] }, layout: "noBorders" },
            { width: "*", table: { widths: ["auto", "*"], body: [
              kv("POWERLINES", opts.content?.environment?.powerlines ?? ""),
              kv("PUBLIC INTERFACE", opts.content?.environment?.public_interface ?? ""),
              kv("WIND (CHART BASIS)", opts.content?.environment?.wind_limit_ms != null
                ? `${opts.content.environment.wind_limit_ms} m/s`
                : (config?.chart_wind_limit_ms ? `${config.chart_wind_limit_ms} m/s (chart limit)` : "—")),
              kv("GROUND GRADE", `${opts.content?.canvas?.ground_gradient_pct ?? 0}%`),
            ] }, layout: "noBorders" },
          ],
          columnGap: 20,
        },
        { text: "Wind shown is the generic chart basis only, not a blanket safe limit for every piece. The governing limit is the LOWEST of: the actual crane's documented limit, the load-specific limit for the effective sail area, shape and mass of each section, the structure supplier's limit, and any site control. To be confirmed by the crane contractor for the actual machine and loads, with the measurement point and stop-work trigger nominated.",
          fontSize: 6.8, color: C.muted, margin: [0, 6, 0, 0] },
        ...(opts.content?.notes
          ? [{ text: opts.content.notes, fontSize: 8.5, margin: [0, 8, 0, 0] }] : []),
      ]),

      section("APPROVALS", [
        {
          table: {
            headerRows: 1,
            widths: ["auto", "*", "*", "*", "auto"],
            body: [
              [th(""), th("NAME"), th("POSITION"), th("SIGNATURE"), th("DATE", "center")],
              [{ text: "Prepared by", fontSize: 8, bold: true },
               { text: dash(opts.preparedBy), fontSize: 8.5, margin: [0, 7, 0, 7] }, "", "", ""],
              [{ text: "Checked by", fontSize: 8, bold: true }, { text: " ", margin: [0, 7, 0, 7] }, "", "", ""],
              [{ text: "Approved (director)", fontSize: 8, bold: true },
               { text: dash(opts.approvedBy), fontSize: 8.5, margin: [0, 7, 0, 7] },
               { text: "Director", fontSize: 8.5 }, "",
               { text: fmtDate(opts.approvedAt), fontSize: 8.5, alignment: "center" }],
              [{ stack: [
                  { text: "Client acknowledgement", fontSize: 8, bold: true },
                  { text: "Acknowledges the preliminary planning basis only. Does not authorise lifting operations.",
                    fontSize: 6.2, color: C.muted, margin: [0, 1, 0, 0] },
                ] }, { text: " ", margin: [0, 7, 0, 7] }, "", "", ""],
            ],
          },
          layout: tableLayout,
        },
      ]),
    ],
  };

  if (isDraft) {
    dd.watermark = { text: "DRAFT — NOT FOR USE", color: C.danger, opacity: 0.08, bold: true };
  }

  const safe = (s: string) => s.replace(/[^a-z0-9-_]+/gi, "_");
  pdfMake.createPdf(dd).download(`${safe(opts.planNumber)}_${safe(opts.title).slice(0, 40)}_v${opts.version}.pdf`);
}
